import apiEndPoints from '../../../routeConfig';
import { ApiService } from '../../../../service/api.service';
import { attempt, resultMessage } from '../../../../service/apiResult';

/**
 * Version store for the eK Score questionnaire catalogue.
 *
 * Backed by /api/v1/ekutir/questionnaire-catalogues, which writes both representations the backend
 * keeps: the `document` jsonb for audit and the exploded category/question/option rows the engine
 * actually loads.
 *
 * The rules below are the server's, restated here only so the screen can disable an action rather
 * than offer it and then report a refusal:
 *  - a published version is immutable, because every stored assessment names the catalogue version
 *    it was scored against;
 *  - publishing retires the outgoing version rather than deleting it, so old assessments stay
 *    resolvable;
 *  - only a draft can be edited or deleted.
 */

const apiService = new ApiService();

const body = (result) => result?.data?.data ?? result?.data ?? null;

export const SEED_VERSION = 'v0.0.1';

export const VERSION_STATUS = {
  CURRENT: 'CURRENT',
  DRAFT: 'DRAFT',
  RETIRED: 'RETIRED',
};

const ok = (data) => ({ success: true, data });
const fail = (message) => ({ success: false, error: { message } });

/** The API returns { summary, document }; the screen has always used one flat record. */
const fromApi = (payload) => {
  const summary = payload?.summary || payload || {};
  return {
    version: summary.version,
    status: summary.status,
    basedOn: summary.basedOn || null,
    publishedAt: summary.publishedAt || null,
    retiredAt: summary.retiredAt || null,
    changeNote: summary.changeNote || '',
    document: payload?.document || null,
  };
};

const summaryFromApi = (summary) => ({
  version: summary.version,
  status: summary.status,
  basedOn: summary.basedOn || null,
  publishedAt: summary.publishedAt || null,
  retiredAt: summary.retiredAt || null,
  changeNote: summary.changeNote || '',
  // The list endpoint returns counts rather than the document; the columns read these.
  categoryCount: summary.categoryCount,
  questionCount: summary.questionCount,
  optionCount: summary.optionCount,
  document: null,
});

/**
 * Deep copy of a catalogue document.
 *
 * A JSON round-trip rather than structuredClone: the document is plain JSON by definition — it is
 * stored in a jsonb column and served as JSON — so there is nothing a round-trip would lose, and it
 * works everywhere, including jsdom, which has no structuredClone.
 */
export function cloneDocument(document) {
  return JSON.parse(JSON.stringify(document));
}

/** A version string is compared component-wise, so v0.0.10 sorts after v0.0.9 rather than before it. */
function versionParts(version) {
  return String(version || '')
    .replace(/^v/i, '')
    .split('.')
    .map((part) => Number.parseInt(part, 10) || 0);
}

export function compareVersions(a, b) {
  const left = versionParts(a);
  const right = versionParts(b);
  const length = Math.max(left.length, right.length);
  for (let i = 0; i < length; i += 1) {
    const diff = (left[i] || 0) - (right[i] || 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

/** Suggested identifier for a duplicate: the highest existing version with its last part bumped. */
export function nextPatchVersion(versions) {
  const highest = [...versions].sort((a, b) => compareVersions(a.version, b.version)).pop();
  const parts = versionParts(highest?.version || SEED_VERSION);
  while (parts.length < 3) parts.push(0);
  parts[parts.length - 1] += 1;
  return `v${parts.join('.')}`;
}

export function isValidVersion(version) {
  return /^v\d+\.\d+\.\d+$/i.test(String(version || '').trim());
}




export async function getVersions() {
  const result = await attempt(() => apiService.get(apiEndPoints.questionnaireCatalogues));
  if (!result.success) return fail(resultMessage(result, 'Could not load questionnaire versions.'));

  return ok((body(result) || []).map(summaryFromApi));
}

export async function getVersion(version) {
  const result = await attempt(() =>
    apiService.get(`${apiEndPoints.questionnaireCatalogues}/${version}`));
  if (!result.success) return fail(resultMessage(result, `Version ${version} could not be loaded.`));

  return ok(fromApi(body(result)));
}

/**
 * Creates a new draft as a deep copy of an existing version's document.
 *
 * The copy is what makes editing safe: a published version's rows must stay exactly as they were
 * when an assessment was scored against them, so a change is always a new version, never an edit
 * in place. Drafts are the only editable rows.
 */
export async function duplicateVersion({ baseVersion, version, changeNote }) {
  const trimmed = String(version || '').trim();
  if (!isValidVersion(trimmed)) return fail('Use a version like v0.0.2.');

  const result = await attempt(() => apiService.post(apiEndPoints.questionnaireCatalogues, {
    baseVersion,
    version: trimmed,
    changeNote: String(changeNote || '').trim() || null,
  }));
  if (!result.success) return fail(resultMessage(result, 'Could not create the new version.'));

  return ok(fromApi(body(result)));
}

export async function saveVersionDocument(version, document) {
  const result = await attempt(() =>
    apiService.update(`${apiEndPoints.questionnaireCatalogues}/${version}`, {
      scale: document?.scale ?? null,
      weighting: document?.weighting ?? null,
      categories: document?.categories || [],
    }));
  if (!result.success) return fail(resultMessage(result, 'Could not save your changes.'));

  return ok(fromApi(body(result)));
}

/**
 * Publishes a draft: it becomes the single current version and the outgoing one is retired.
 *
 * Mirrors the backend's partial unique index on is_current — exactly one current catalogue, and
 * the previous one is retired rather than deleted.
 */
export async function publishVersion(version) {
  const result = await attempt(() =>
    apiService.post(`${apiEndPoints.questionnaireCatalogues}/${version}/publish`, {}));
  if (!result.success) return fail(resultMessage(result, `Could not publish ${version}.`));

  return ok(fromApi(body(result)));
}

export async function deleteVersion(version) {
  const result = await attempt(() =>
    apiService.delete(`${apiEndPoints.questionnaireCatalogues}/${version}`));
  if (!result.success) return fail(resultMessage(result, `Could not delete ${version}.`));

  return ok(null);
}

/* ---------- document helpers ---------- */

export function countQuestions(document) {
  return (document?.categories || []).reduce(
    (total, category) => total + (category.questions?.length || 0),
    0
  );
}

export function countOptions(document) {
  return (document?.categories || []).reduce(
    (total, category) =>
      total +
      (category.questions || []).reduce((sum, question) => sum + (question.options?.length || 0), 0),
    0
  );
}

/**
 * A configured weight exactly as the catalogue states it (15, 1.5), never as a derived share.
 * Rounded to two decimals only to hide float noise from summing, e.g. 0.1 + 0.2.
 */
export function formatWeight(value) {
  const number = Number(value) || 0;
  return String(Math.round(number * 100) / 100);
}

/** Sum of every category's globalWeightPct: the denominator the catalogue's weighting rule names. */
export function globalWeightTotal(document) {
  return (document?.categories || []).reduce(
    (sum, category) => sum + (Number(category.globalWeightPct) || 0),
    0
  );
}

/** Sum of a category's intraCategoryWeight values: the denominator one level down. */
export function intraCategoryWeightTotal(category) {
  return (category?.questions || []).reduce(
    (sum, question) => sum + (Number(question.intraCategoryWeight) || 0),
    0
  );
}

/**
 * Effective share of the final score per category.
 *
 * The configured globalWeightPct is the source of truth and is what the screen shows first. It is
 * deliberately not constrained to sum to 100 (the seeded catalogue's eight categories sum to 110),
 * and the catalogue's own weighting rule says to normalize at compute time as
 * categoryWeight / sum(all categories' globalWeightPct). The engine does exactly that; this
 * reproduces it so the screen can show the share next to the configured weight, never instead of it.
 */
export function effectiveWeights(document) {
  const categories = document?.categories || [];
  const total = globalWeightTotal(document);
  return categories.reduce((acc, category) => {
    const raw = Number(category.globalWeightPct) || 0;
    acc[category.categoryCode] = total > 0 ? (raw / total) * 100 : 0;
    return acc;
  }, {});
}

/** Same normalization one level down: a question's share within its own category. */
export function effectiveQuestionWeights(category) {
  const questions = category?.questions || [];
  const total = intraCategoryWeightTotal(category);
  return questions.reduce((acc, question) => {
    const raw = Number(question.intraCategoryWeight) || 0;
    acc[question.questionCode] = total > 0 ? (raw / total) * 100 : 0;
    return acc;
  }, {});
}

/**
 * Blocks publishing a catalogue the engine could not score against.
 *
 * Codes matter more than labels here: answers are stored by question/option code, so a duplicate
 * code inside a version makes a stored answer ambiguous.
 */
export function validateDocument(document) {
  const issues = [];
  const categories = document?.categories || [];

  if (!categories.length) issues.push('Add at least one category before publishing.');

  const questionCodes = new Set();
  const categoryCodes = new Set();

  categories.forEach((category) => {
    const categoryName = category.label || category.categoryCode || 'A category';

    if (!category.categoryCode?.trim()) issues.push(`${categoryName} needs a category code.`);
    else if (categoryCodes.has(category.categoryCode)) {
      issues.push(`Category code ${category.categoryCode} is used more than once.`);
    } else categoryCodes.add(category.categoryCode);

    if (!(Number(category.globalWeightPct) > 0)) {
      issues.push(`${categoryName} needs a global weight above 0.`);
    }
    if (!category.questions?.length) {
      issues.push(`${categoryName} needs at least one question.`);
    }

    (category.questions || []).forEach((question) => {
      const questionName = question.questionCode || 'A question';

      if (!question.questionCode?.trim()) issues.push(`A question in ${categoryName} needs a code.`);
      else if (questionCodes.has(question.questionCode)) {
        issues.push(`Question code ${question.questionCode} is used more than once.`);
      } else questionCodes.add(question.questionCode);

      if (!question.text?.trim()) issues.push(`${questionName} needs question text.`);
      if (!(Number(question.intraCategoryWeight) > 0)) {
        issues.push(`${questionName} needs a weight above 0.`);
      }
      if ((question.options || []).length < 2) {
        issues.push(`${questionName} needs at least two answers.`);
      }

      const optionCodes = new Set();
      (question.options || []).forEach((option) => {
        if (!option.optionCode?.trim()) issues.push(`An answer on ${questionName} needs a code.`);
        else if (optionCodes.has(option.optionCode)) {
          issues.push(`Answer code ${option.optionCode} is used twice on ${questionName}.`);
        } else optionCodes.add(option.optionCode);

        if (!option.label?.trim()) issues.push(`An answer on ${questionName} needs a label.`);

        const risk = Number(option.riskValue);
        if (Number.isNaN(risk) || risk < 0 || risk > 10) {
          issues.push(`An answer on ${questionName} needs a risk value between 0 and 10.`);
        }
      });
    });
  });

  return issues;
}
